/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */

'use es6';

// Prevent unwanted focus styles
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-vr-focus", "aria-label", "aria-labelledby", "autoFocus", "checked", "children", "className", "color", "description", "disabled", "id", "indeterminate", "name", "onChange", "readOnly", "size", "tabIndex", "value"];
import '../listeners/focusStylesListener';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useContext } from 'react';
import devLogger from 'react-utils/devLogger';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import memoize from 'react-utils/memoize';
import styled, { css } from 'styled-components';
import { BATTLESHIP, CALYPSO, KOALA, OLAF } from 'HubStyleTokens/colors';
import { TOGGLE_BUTTON_SIZE } from 'HubStyleTokens/sizes';
import { FieldsetContext } from '../context/FieldsetContext';
import Checkable from '../decorators/Checkable';
import UICheckmark from '../icon/icons/UICheckmark';
import { propTypeForSizes } from '../utils/propTypes/tshirtSize';
import { getTabIndex } from '../utils/TabIndex';
import { ALIGNMENT_CLASSES } from './CheckboxConstants';
import ToggleInputWrapper from './internal/ToggleInputWrapper';
import { setUiTransition, uiFocus } from '../utils/Styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Checkbox = styled(ToggleInputWrapper).withConfig({
  displayName: "UICheckbox__Checkbox",
  componentId: "sc-17asv8o-0"
})(["user-select:none;", ";", ";", ";"], props => props.description && props.descriptionLayout === 'vertical' && css(["flex-direction:column;flex-wrap:wrap;margin-top:6px;"]), props => props.hasChildren && css(["height:", ";width:", ";"], TOGGLE_BUTTON_SIZE, TOGGLE_BUTTON_SIZE), props => (props.hasChildren && !props.inline || props.innerPadding === 'none') && css(["display:block;min-height:0;"]));
const PrivateCheckboxIndicator = styled.span.withConfig({
  displayName: "UICheckbox__PrivateCheckboxIndicator",
  componentId: "sc-17asv8o-1"
})(["", " flex-shrink:0;background-color:", ";color:", ";height:1.25em;left:0.0625em;position:relative;width:1.25em;border:2px solid ", ";border-radius:3px;"], setUiTransition(), OLAF, BATTLESHIP, BATTLESHIP);
const PrivateCheckboxIconInner = styled(UICheckmark).withConfig({
  displayName: "UICheckbox__PrivateCheckboxIconInner",
  componentId: "sc-17asv8o-2"
})(["fill:currentColor;width:1em;"]);
const PrivateCheckboxIconInnerSVG = styled.path.withConfig({
  displayName: "UICheckbox__PrivateCheckboxIconInnerSVG",
  componentId: "sc-17asv8o-3"
})(["fill:currentColor;width:1em;"]);
const PrivateCheckboxIcon = styled.span.withConfig({
  displayName: "UICheckbox__PrivateCheckboxIcon",
  componentId: "sc-17asv8o-4"
})(["align-items:center;display:flex;justify-content:center;bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;> svg{fill:currentColor;width:94%;}"]);
const PrivateCheckboxInput = styled.input.withConfig({
  displayName: "UICheckbox__PrivateCheckboxInput",
  componentId: "sc-17asv8o-5"
})(["&:checked{+ ", "{", "{fill:", ";opacity:1;}.private-checkbox__dash{opacity:0;}}}&.indeterminate:not(:checked){+ ", "{.private-checkbox__check{opacity:0;}.private-checkbox__dash{fill:", ";opacity:1;> svg{width:100%;}}}}&:checked,&.indeterminate{+ ", "{border-color:currentColor;color:", ";}}&:focus:not(:checked){+ ", "{border-color:", ";}}.hubspot-enable-focus-styles &:focus{&:not([readonly]) + ", "{", ";background-color:", ";border-color:currentColor;}&[readonly] + ", "{outline:auto;}}"], PrivateCheckboxIndicator, PrivateCheckboxIcon, CALYPSO, PrivateCheckboxIndicator, CALYPSO, PrivateCheckboxIndicator, CALYPSO, PrivateCheckboxIndicator, BATTLESHIP, PrivateCheckboxIndicator, uiFocus, KOALA, PrivateCheckboxIndicator);
const renderCheckmark = memoize(() => /*#__PURE__*/_jsx(PrivateCheckboxIconInner, {
  className: "private-checkbox__icon__inner"
}));
const renderDash = memoize(() => /*#__PURE__*/_jsx("svg", {
  role: "presentation",
  viewBox: "0 0 14 14",
  xmlns: "http://www.w3.org/2000/svg",
  children: /*#__PURE__*/_jsx(PrivateCheckboxIconInnerSVG, {
    className: "private-checkbox__icon__inner",
    d: "M 2 6 L12 6 L12 9 L2 9 L2 6 z"
  })
}));
const renderIndicator = memoize(color => /*#__PURE__*/_jsxs(PrivateCheckboxIndicator, {
  className: "private-checkbox__indicator",
  "data-indicator": true,
  style: {
    color
  },
  children: [/*#__PURE__*/_jsx(PrivateCheckboxIcon, {
    className: "private-checkbox__check",
    children: renderCheckmark()
  }), /*#__PURE__*/_jsx(PrivateCheckboxIcon, {
    className: "private-checkbox__dash",
    children: renderDash()
  })]
}));
const UICheckbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'data-vr-focus': dataVrFocus,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus,
      checked,
      children,
      className,
      color,
      description,
      disabled,
      id,
      indeterminate,
      name,
      onChange,
      readOnly,
      size,
      tabIndex,
      value
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  if (process.env.NODE_ENV !== 'production') {
    if (children == null && !ariaLabel && !ariaLabelledBy) {
      devLogger.warn({
        message: "UICheckbox: `aria-label` or `aria-labelledby` is required if you don't define `children`.",
        key: 'UICheckbox: no label'
      });
    }
    if (ariaLabel && (children || ariaLabelledBy)) {
      devLogger.warn({
        message: 'UICheckbox: `aria-label` should not be used with `children` or `aria-labelledby`.',
        key: 'UICheckbox: aria-label hides children',
        url: 'https://git.hubteam.com/HubSpot/UIComponents/pull/8473/files#r1011220'
      });
    }
  }
  const descriptionId = useUniqueId('checkbox-');
  const childWrapperId = useUniqueId('checkbox-content-');
  const computedDisabled = disabled || fieldDisabled;
  const computedSize = size || fieldSize;
  const ariaLabelledByIds = classNames(ariaLabelledBy, children && childWrapperId);
  const indeterminateAttributes = indeterminate && !checked && {
    'aria-checked': 'mixed'
  };
  return /*#__PURE__*/_jsx(Checkbox, Object.assign({}, rest, {
    checked: checked,
    childWrapperId: childWrapperId,
    className: classNames(className, 'private-checkbox'),
    description: description,
    descriptionId: descriptionId,
    disabled: computedDisabled,
    hasChildren: children == null,
    input: /*#__PURE__*/_jsx(PrivateCheckboxInput, Object.assign({
      "data-vr-focus": dataVrFocus,
      "aria-describedby": description ? descriptionId : null,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledByIds,
      autoFocus: autoFocus,
      checked: checked,
      className: 'private-checkbox__input sr-only' + (indeterminate ? " indeterminate" : ""),
      disabled: computedDisabled,
      id: id,
      name: name,
      onChange: evt => {
        if (disabled || readOnly) {
          evt.preventDefault();
          return;
        }
        onChange(evt);
      },
      readOnly: readOnly,
      tabIndex: getTabIndex(readOnly || disabled, tabIndex),
      type: "checkbox",
      value: value
    }, indeterminateAttributes)),
    indicator: renderIndicator(color),
    onChange: onChange,
    readOnly: readOnly,
    ref: ref,
    size: computedSize,
    children: children
  }));
});
UICheckbox.propTypes = {
  alignment: PropTypes.oneOf(Object.keys(ALIGNMENT_CLASSES)),
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  autoFocus: PropTypes.bool,
  checked: PropTypes.bool,
  children: PropTypes.node,
  color: PropTypes.string,
  description: PropTypes.node,
  descriptionLayout: PropTypes.oneOf(['horizontal', 'vertical']),
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  inline: PropTypes.bool,
  innerPadding: PropTypes.oneOf(['default', 'none']),
  labelClassName: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOfType([propTypeForSizes(['sm']), PropTypes.oneOf(['default'])]),
  value: PropTypes.any
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UICheckbox.defaultProps = {
  alignment: 'start',
  checked: false,
  descriptionLayout: 'vertical',
  innerPadding: 'default'
};
UICheckbox.displayName = 'UICheckbox';
export default Checkable(UICheckbox);